import { AxiosError } from 'axios';
import { CommunicationLevels } from 'shared/enums';
import {
  CommunicationTemplateLevelFilter,
  ICommunicationTemplate,
  ICommunicationTemplateDetail,
  ICommunicationTemplateOverride,
  ICommunicationTemplateOverrideVersion,
  ICommunicationTemplatePreviewPayload,
  ICommunicationTemplatePreviewResponse
} from 'shared/interfaces/communicationTemplate.interface';
import { http } from 'shared/utils/axios';

const BASE_TEMPLATE_PATH = '/communicationTemplates';

type TemplateListParams = {
  communicationLevel?: CommunicationTemplateLevelFilter;
  search?: string;
  includeInactive?: boolean;
};

type OverrideUpsertPayload = {
  subject: string;
  htmlBody: string;
  project?: string | null;
  overrideId?: string | null;
  active?: boolean;
};

type TemplateUpdatePayload = {
  enabled: boolean;
};

type SendTestPayload = {
  recipient: string;
  project?: string;
  data?: Record<string, unknown>;
  subject?: string;
  htmlBody?: string;
};

class CommunicationTemplatesService {
  private static instance: CommunicationTemplatesService;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): CommunicationTemplatesService {
    if (!CommunicationTemplatesService.instance) {
      CommunicationTemplatesService.instance =
        new CommunicationTemplatesService();
    }

    return CommunicationTemplatesService.instance;
  }

  getAllCommunicationTemplates = async (params?: TemplateListParams) => {
    const query: Record<string, string | boolean> = {};

    if (params?.communicationLevel) {
      query.communicationLevel = params.communicationLevel;
    }

    if (params?.search) {
      query.search = params.search;
    }

    if (typeof params?.includeInactive === 'boolean') {
      query.includeInactive = params.includeInactive;
    }

    if (!Object.keys(query).length) {
      query.communicationLevel = CommunicationLevels.Affiliate;
    }

    const { data } = await http<ICommunicationTemplate[]>({
      url: BASE_TEMPLATE_PATH,
      method: 'GET',
      params: query
    });
    return data;
  };

  getTemplateDetailById = async (id: string) => {
    const { data } = await http<ICommunicationTemplateDetail>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(id)}`,
      method: 'GET'
    });
    return data;
  };

  updateTemplateById = async (id: string, payload: TemplateUpdatePayload) => {
    const { data } = await http<ICommunicationTemplateDetail>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(id)}`,
      method: 'PUT',
      data: payload
    });
    return data;
  };

  getTemplateDetailByKey = async (key: string) => {
    const { data } = await http<ICommunicationTemplateDetail>({
      url: `${BASE_TEMPLATE_PATH}/key/${encodeURIComponent(key)}`,
      method: 'GET'
    });
    return data;
  };

  getOverridesByKey = async (key: string) => {
    const { data } = await http<any[]>({
      url: `${BASE_TEMPLATE_PATH}/key/${encodeURIComponent(key)}/overrides`,
      method: 'GET'
    });
    return data;
  };

  getByKey = async (key: string) => {
    const { data } = await http<any | null>({
      url: `${BASE_TEMPLATE_PATH}/key/${encodeURIComponent(key)}`,
      method: 'GET'
    });
    return data;
  };

  getSourceTemplateByKey = async (key: string) => {
    const { data } = await http<any | null>({
      url: `${BASE_TEMPLATE_PATH}/key/${encodeURIComponent(key)}/source`,
      method: 'GET'
    });
    return data;
  };

  getOverridesByTemplateId = async (templateId: string) => {
    const { data } = await http<ICommunicationTemplateOverride[]>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(templateId)}/overrides`,
      method: 'GET'
    });
    return Array.isArray(data) ? data : [];
  };

  getOverrideById = async (templateId: string, overrideId: string) => {
    const { data } = await http<ICommunicationTemplateOverride>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(
        templateId
      )}/overrides/${encodeURIComponent(overrideId)}`,
      method: 'GET'
    });
    return data;
  };

  upsertOverride = async (
    templateId: string,
    payload: OverrideUpsertPayload
  ) => {
    const { data } = await http<ICommunicationTemplateOverride>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(templateId)}/overrides`,
      method: 'POST',
      data: payload
    });
    return data;
  };

  deleteOverride = async (templateId: string, overrideId: string) => {
    await http<void>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(
        templateId
      )}/overrides/${encodeURIComponent(overrideId)}`,
      method: 'DELETE'
    });
  };

  deactivateOverride = async (templateId: string, overrideId: string) => {
    await this.deleteOverride(templateId, overrideId);
  };

  getOverrideVersions = async (templateId: string, overrideId: string) => {
    const { data } = await http<unknown>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(
        templateId
      )}/overrides/${encodeURIComponent(overrideId)}/versions`,
      method: 'GET'
    });
    const normaliseCreatedAt = (input: unknown) => {
      if (typeof input === 'string') {
        return input;
      }
      if (input && typeof input === 'object') {
        const withDate = input as { $date?: unknown };
        if (withDate.$date && typeof withDate.$date === 'string') {
          return withDate.$date;
        }
      }
      return '';
    };

    const processVersion = (
      item: unknown,
      index: number
    ): ICommunicationTemplateOverrideVersion | null => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const candidate = item as Record<string, unknown>;
      const rawVersion = candidate['version'];
      let numericVersion: number | undefined;
      if (typeof rawVersion === 'number') {
        numericVersion = rawVersion;
      } else if (rawVersion != null) {
        numericVersion = Number(rawVersion);
      } else {
        numericVersion = undefined;
      }

      const subject =
        typeof candidate['subject'] === 'string'
          ? (candidate['subject'] as string)
          : '';
      const htmlBody =
        typeof candidate['htmlBody'] === 'string'
          ? (candidate['htmlBody'] as string)
          : '';
      const createdAt = normaliseCreatedAt(candidate['createdAt']);

      let id: string;
      if (typeof candidate['id'] === 'string') {
        id = candidate['id'] as string;
      } else if (typeof candidate['_id'] === 'string') {
        id = candidate['_id'] as string;
      } else {
        id = `${numericVersion ?? index}-${index}`;
      }

      const normalised: ICommunicationTemplateOverrideVersion = {
        id,
        version: numericVersion ?? index,
        subject,
        htmlBody,
        createdAt
      };

      if (
        candidate['createdBy'] &&
        typeof candidate['createdBy'] === 'object'
      ) {
        normalised.createdBy = candidate[
          'createdBy'
        ] as ICommunicationTemplateOverrideVersion['createdBy'];
      }
      if (typeof candidate['summary'] === 'string') {
        normalised.summary = candidate['summary'] as string;
      }

      return normalised;
    };

    const normalizeTemplateVersions = (
      value: unknown
    ): ICommunicationTemplateOverrideVersion[] => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value
        .map((item, index) => processVersion(item, index))
        .filter(
          (item): item is ICommunicationTemplateOverrideVersion => !!item
        );
    };

    if (Array.isArray(data)) {
      return normalizeTemplateVersions(data);
    }

    if (data && typeof data === 'object') {
      const typed = data as Record<string, unknown>;
      if (Array.isArray(typed['revisions'])) {
        return normalizeTemplateVersions(typed['revisions']);
      }
      if (
        typed['data'] &&
        typeof typed['data'] === 'object' &&
        Array.isArray((typed['data'] as Record<string, unknown>)['revisions'])
      ) {
        return normalizeTemplateVersions(
          (typed['data'] as Record<string, unknown>)['revisions'] as unknown[]
        );
      }
    }

    return [];
  };

  revertOverrideVersion = async (
    templateId: string,
    overrideId: string,
    version: string | number
  ) => {
    const { data } = await http<ICommunicationTemplateOverride>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(
        templateId
      )}/overrides/${encodeURIComponent(
        overrideId
      )}/revert/${encodeURIComponent(String(version))}`,
      method: 'POST'
    });
    return data;
  };

  sendTestByKey = async (
    key: string,
    payload: {
      recipient: string;
      project?: string;
      data?: Record<string, unknown>;
    }
  ) => {
    const { data } = await http<{ success: boolean }>({
      url: `${BASE_TEMPLATE_PATH}/key/${encodeURIComponent(key)}/test-send`,
      method: 'POST',
      data: payload
    });
    return data;
  };

  sendTestById = async (templateId: string, payload: SendTestPayload) => {
    const { data } = await http<{ success: boolean }>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(templateId)}/test-send`,
      method: 'POST',
      data: payload
    });
    return data;
  };

  previewById = async (
    id: string,
    payload?: ICommunicationTemplatePreviewPayload
  ) => {
    const { data } = await http<ICommunicationTemplatePreviewResponse>({
      url: `${BASE_TEMPLATE_PATH}/${encodeURIComponent(id)}/preview`,
      method: 'POST',
      data: payload || {}
    });
    return data;
  };

  mapHttpError = (error: unknown) => {
    const fallback = {
      message: 'Unexpected error. Please try again.',
      status: undefined as number | undefined,
      details: undefined as unknown
    };

    if (!error) return fallback;

    if ((error as AxiosError)?.isAxiosError) {
      const axiosError = error as AxiosError<{ message?: string; error?: any }>;
      return {
        message:
          axiosError.response?.data?.message ||
          axiosError.message ||
          fallback.message,
        status: axiosError.response?.status,
        details: axiosError.response?.data?.error
      };
    }

    if (error instanceof Error) {
      return { ...fallback, message: error.message };
    }

    return fallback;
  };
}

const instance = CommunicationTemplatesService.getInstance();

export default instance;
